import { brandText, currentLang, t } from '../i18n/index.ts';

/** Talks to the ERP PRO server. Errors carry the server's message, in the language of this device. */

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown> | undefined;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const TOKEN_KEY = 'erp.token';

/** When the person last touched this screen: office sign-ins end after a while unused (platform.ts). */
let lastInput = Date.now();
if (typeof window !== 'undefined') {
  const touched = () => {
    lastInput = Date.now();
  };
  for (const e of ['pointerdown', 'keydown', 'wheel', 'touchstart']) window.addEventListener(e, touched, { capture: true, passive: true });
}
/** Milliseconds since the person last touched this screen. */
export const idleFor = () => Date.now() - lastInput;
export const idleHeader = () => ({ 'x-erp-idle-ms': String(idleFor()) });

export const tokenStore = {
  get: (): string | null => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (t: string | null) => {
    try {
      if (t) localStorage.setItem(TOKEN_KEY, t);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Private mode: the session lasts until the tab closes.
    }
  },
};

let onUnauthorized: () => void = () => {};
let onConnection: (online: boolean) => void = () => {};
/** Told after every call whether the server could be reached (the offline till listens). */
export const setConnectionHandler = (fn: (online: boolean) => void) => {
  onConnection = fn;
};
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = tokenStore.get();
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: {
        'Accept-Language': currentLang(),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}`, ...idleHeader() } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    onConnection(false);
    throw new ApiError(0, 'offline', 'offline');
  }
  // A proxy answering for a server that is down is no connection either.
  onConnection(!(res.status === 502 || res.status === 503 || res.status === 504));
  if (res.status === 204) return undefined as T;
  // The answer cut off half-way (the connection dropped, or the page is being left) is no answer:
  // it must never reach a screen as if it were the data asked for.
  let text: string;
  try {
    text = await res.text();
  } catch {
    throw new ApiError(0, 'offline', 'offline');
  }
  let data: any = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (res.ok) throw new ApiError(res.status, 'error', t('error.generic'));
    }
  } else if (res.ok) return undefined as T;
  if (!res.ok) {
    if (res.status === 401 && path !== '/api/auth/login') onUnauthorized();
    const e = data.error ?? {};
    throw new ApiError(res.status, e.code ?? 'error', e.message ? brandText(e.message) : t('error.generic'), e.details);
  }
  return data as T;
}

/** Downloads a file the server makes (e.g. a data export), with this sign-in, under its name. */
export async function download(path: string, headers: Record<string, string> = {}): Promise<void> {
  const token = tokenStore.get();
  const res = await fetch(path, { headers: { 'Accept-Language': currentLang(), ...(token ? { Authorization: `Bearer ${token}`, ...idleHeader() } : {}), ...headers } });
  if (!res.ok) {
    const e = (await res.json().catch(() => ({}))).error ?? {};
    throw new ApiError(res.status, e.code ?? 'error', e.message ? brandText(e.message) : t('error.generic'), e.details);
  }
  await saveBlob(await res.blob(), /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'export.zip');
}
export async function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const action = <T = any>(name: string, input: Record<string, unknown> = {}) => request<T>('POST', `/api/actions/${name}`, input);
/** An action that takes a file (its input may be up to 12 MB). */
export const upload = <T = any>(name: string, input: Record<string, unknown> = {}) => request<T>('POST', `/api/upload/${name}`, input);

export const records = {
  list: <T = any>(model: string, query: Record<string, string> = {}) => request<T[]>('GET', `/api/records/${model}?${new URLSearchParams(query)}`),
  create: <T = any>(model: string, values: Record<string, unknown>) => request<T>('POST', `/api/records/${model}`, values),
  update: <T = any>(model: string, id: string, values: Record<string, unknown>) => request<T>('PATCH', `/api/records/${model}/${id}`, values),
  remove: (model: string, id: string) => request<void>('DELETE', `/api/records/${model}/${id}`),
};

/**
 * Live events over Server-Sent Events, read with fetch so the token travels in a
 * header and never in a URL. Every part of the screen that listens shares one
 * connection for all their topics: a browser allows only six open connections to a
 * server, and a page with a menu badge or two and its own live list would otherwise
 * run out of them. When the topics change, the new connection opens before the old
 * one closes, so nothing is missed. Reconnects with back-off; returns a stop function.
 */
interface Listener {
  topics: string[];
  onEvent: (event: string, data: any) => void;
  onState?: (live: boolean) => void;
  /** Has not had its "ready" yet. */
  fresh: boolean;
  live: boolean;
}
const listeners = new Set<Listener>();
let current: { key: string; controller: AbortController } | null = null;
let retiring: AbortController[] = [];
let connected = false;
let pending: ReturnType<typeof setTimeout> | null = null;

const tell = (l: Listener, live: boolean) => {
  if (l.live === live) return;
  l.live = live;
  l.onState?.(live);
};
const setConnected = (live: boolean) => {
  connected = live;
  for (const l of [...listeners]) tell(l, live);
};
const hears = (l: Listener, event: string) => (event === 'ready' ? l.fresh : l.topics.some((t) => event === t || event.startsWith(`${t}.`)));

function reconcile() {
  pending = null;
  const key = [...new Set([...listeners].flatMap((l) => l.topics))].sort().join(',');
  if (!key) {
    for (const c of retiring) c.abort();
    retiring = [];
    current?.controller.abort();
    current = null;
    connected = false;
    return;
  }
  if (current?.key === key) return;
  if (current) retiring.push(current.controller);
  connect(key);
}

function connect(key: string) {
  const controller = new AbortController();
  current = { key, controller };
  let delay = 1000;
  const mine = () => current?.controller === controller;
  void (async () => {
    while (mine()) {
      try {
        const res = await fetch(`/api/events?topics=${encodeURIComponent(key)}`, {
          headers: { Authorization: `Bearer ${tokenStore.get() ?? ''}`, ...idleHeader() },
          signal: controller.signal,
        });
        if (res.status === 401) {
          onUnauthorized();
          return;
        }
        if (!res.ok || !res.body) throw new Error('stream failed');
        delay = 1000;
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += value;
          let i: number;
          while ((i = buffer.indexOf('\n\n')) >= 0) {
            const chunk = buffer.slice(0, i);
            buffer = buffer.slice(i + 2);
            let event = 'message';
            let data = '';
            for (const line of chunk.split('\n')) {
              if (line.startsWith('event: ')) event = line.slice(7);
              else if (line.startsWith('data: ')) data += line.slice(6);
            }
            if (!data) continue;
            if (event === 'ready') {
              if (!mine()) continue;
              // The new connection hears everything now: the old ones can go.
              for (const c of retiring) c.abort();
              retiring = [];
              setConnected(true);
            }
            const payload = JSON.parse(data);
            for (const l of [...listeners]) {
              if (!hears(l, event)) continue;
              if (event === 'ready') l.fresh = false;
              l.onEvent(event, payload);
            }
          }
        }
      } catch {
        // fall through to reconnect
      }
      if (!mine()) return;
      setConnected(false);
      await new Promise((r) => setTimeout(r, delay));
      delay = Math.min(delay * 2, 15000);
    }
  })();
}

export function subscribe(topics: string[], onEvent: (event: string, data: any) => void, onState?: (live: boolean) => void) {
  const l: Listener = { topics, onEvent, onState, fresh: true, live: false };
  listeners.add(l);
  // Already covered by the open connection: no need to wait for a new one.
  if (connected && current && topics.every((t) => current!.key.split(',').includes(t))) {
    l.fresh = false;
    queueMicrotask(() => {
      if (!listeners.has(l)) return;
      onEvent('ready', {});
      tell(l, true);
    });
  }
  // Screens mount several listeners at once: change the connection once for all of them.
  if (!pending) pending = setTimeout(reconcile, 30);
  return () => {
    listeners.delete(l);
    if (!pending) pending = setTimeout(reconcile, 30);
  };
}
